// Auth page: Sign in / Sign up with email and password; Turnstile on signup
// Set to false to show waitlist ("notify when site is up") instead of signup form
const SIGNUP_ENABLED = false;

import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Turnstile } from "@marsidev/react-turnstile";
import { API_BASE_URL } from "../config/apiConfig";

const BRAND = {
  bg: "#0a0a0f",
  bgSecondary: "#111118",
  panel: "#1a1a24",
  panelHover: "#22222f",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  accent: "#6366f1",
  accentHover: "#818cf8",
  danger: "#ef4444",
};

const AUTH_REQUEST_TIMEOUT = 15000; // 15 seconds

/** Shown on timeout / network error — localhost is only for your machine, not production. */
function networkFailureMessage() {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") {
      return "Request timed out or server unreachable. For local dev, run the API (e.g. uvicorn on port 8000).";
    }
  }
  return "Request timed out or server unreachable. In Railway: set REACT_APP_API_BASE_URL to your backend's public URL at build time, redeploy the frontend, and ensure the backend service is running.";
}

const siteFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Inter', 'Helvetica Neue', sans-serif";

const inputStyle = (hasError) => ({
  width: "100%",
  padding: "12px 16px",
  background: BRAND.bgSecondary,
  border: `1px solid ${hasError ? BRAND.danger : BRAND.border}`,
  borderRadius: "8px",
  color: BRAND.text,
  fontSize: "16px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: siteFont,
});

export default function AuthPage() {
  const [tab, setTab] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const turnstileRef = useRef(null);
  const navigate = useNavigate();

  const resetForm = () => {
    setError("");
    setPassword("");
    setPasswordConfirm("");
    setTurnstileToken("");
    turnstileRef.current?.reset();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { timeout: AUTH_REQUEST_TIMEOUT }
      );
      localStorage.setItem("authToken", data.access_token);
      localStorage.setItem("authTimestamp", Date.now().toString());
      localStorage.setItem("authUser", JSON.stringify(data.user));
      navigate("/", { replace: true });
      window.location.reload(); // refresh so header shows authenticated state
    } catch (err) {
      const msg = err.code === "ECONNABORTED" || err.message === "Network Error"
        ? "Request timed out or server unreachable. Is the backend running on http://localhost:8000?"
        : (err.response?.data?.detail || "Sign in failed. Check your email and password.");
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (tab === "signup" && !turnstileToken && process.env.REACT_APP_TURNSTILE_SITE_KEY) {
      setError("Please complete the verification check.");
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/signup`,
        {
          email: email.trim().toLowerCase(),
          password,
          password_confirm: passwordConfirm,
          turnstile_token: turnstileToken || null,
        },
        { timeout: AUTH_REQUEST_TIMEOUT }
      );
      if (!data || !data.access_token) {
        setError("Invalid response from server. Try again.");
        return;
      }
      localStorage.setItem("authToken", data.access_token);
      localStorage.setItem("authTimestamp", Date.now().toString());
      localStorage.setItem("authUser", JSON.stringify(data.user || {}));
      navigate("/select-plan", { replace: true });
      window.location.reload();
    } catch (err) {
      const msg = err.code === "ECONNABORTED" || err.message === "Network Error"
        ? networkFailureMessage()
        : (err.response?.data?.detail || "Sign up failed. Try again.");
      setError(msg);
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (tab === "signin") handleSignIn(e);
    else handleSignUp(e);
  };

  // Waitlist (when signup is disabled)
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const [notifyError, setNotifyError] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    setNotifyError("");
    if (!notifyEmail.trim()) return;
    setNotifyLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/waitlist`,
        { email: notifyEmail.trim().toLowerCase() },
        { timeout: AUTH_REQUEST_TIMEOUT }
      );
      setNotifySent(true);
      setNotifyEmail("");
    } catch (err) {
      setNotifyError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const showSignupDisabled = tab === "signup" && !SIGNUP_ENABLED;
  const logoUrl = "/fs-logo.png";
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: BRAND.bg,
        backgroundImage:
          "radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)",
        padding: "20px",
        fontFamily: siteFont,
      }}
    >
      <Link
        to="/"
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          color: BRAND.textSecondary,
          fontSize: 14,
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        ← Back to home
      </Link>
      <div
        style={{
          background: BRAND.panel,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 16,
          padding: "40px 48px",
          maxWidth: "420px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Link to="/" style={{ display: "inline-block", marginBottom: 16 }}>
            <img src={logoUrl} alt="Fundamental Signals" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ height: 44, objectFit: "contain" }} />
          </Link>
          <h1 style={{ margin: "0 0 8px", fontSize: "24px", fontWeight: 600, color: BRAND.text }}>
            {showSignupDisabled ? "Sign up" : tab === "signin" ? "Sign in" : "Create account"}
          </h1>
          <p style={{ margin: 0, fontSize: "14px", color: BRAND.textSecondary }}>
            {showSignupDisabled ? "New accounts are coming soon." : tab === "signin" ? "Sign in with your email and password." : "Sign up with email — no username required."}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <button
            type="button"
            onClick={() => { setTab("signin"); resetForm(); setNotifySent(false); setNotifyError(""); }}
            style={{
              flex: 1,
              padding: "10px 16px",
              background: tab === "signin" ? BRAND.accent : BRAND.bgSecondary,
              border: `1px solid ${tab === "signin" ? BRAND.accent : BRAND.border}`,
              borderRadius: 8,
              color: BRAND.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => { setTab("signup"); resetForm(); setNotifySent(false); setNotifyError(""); }}
            style={{
              flex: 1,
              padding: "10px 16px",
              background: tab === "signup" ? BRAND.accent : BRAND.bgSecondary,
              border: `1px solid ${tab === "signup" ? BRAND.accent : BRAND.border}`,
              borderRadius: 8,
              color: BRAND.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign up
          </button>
        </div>

        {showSignupDisabled ? (
          <>
            <p style={{ margin: "0 0 20px", fontSize: 15, color: BRAND.textSecondary, textAlign: "center" }}>
              We're not accepting new signups right now. Enter your email and we'll notify you when the website is up and running.
            </p>
            {notifySent ? (
              <div style={{ padding: 16, background: "rgba(16, 185, 129, 0.15)", border: "1px solid #10b981", borderRadius: 8, color: "#10b981", fontSize: 14, textAlign: "center" }}>
                Thanks! We'll notify you when signup is available.
              </div>
            ) : (
              <form onSubmit={handleNotifySubmit}>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => { setNotifyEmail(e.target.value); setNotifyError(""); }}
                    placeholder="your@email.com"
                    required
                    disabled={notifyLoading}
                    style={inputStyle(!!notifyError)}
                  />
                </div>
                {notifyError && (
                  <div style={{ padding: 12, marginBottom: 12, background: "rgba(239, 68, 68, 0.1)", border: `1px solid ${BRAND.danger}`, borderRadius: 8, color: BRAND.danger, fontSize: 14, textAlign: "center" }}>
                    {notifyError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={notifyLoading || !notifyEmail.trim()}
                  style={{
                    width: "100%",
                    padding: 14,
                    background: notifyLoading || !notifyEmail.trim() ? BRAND.border : BRAND.accent,
                    color: BRAND.text,
                    border: "none",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: notifyLoading || !notifyEmail.trim() ? "not-allowed" : "pointer",
                    opacity: notifyLoading || !notifyEmail.trim() ? 0.7 : 1,
                  }}
                >
                  {notifyLoading ? "Sending..." : "Notify me"}
                </button>
              </form>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="email" style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500, color: BRAND.text }}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                placeholder="you@example.com"
                required
                disabled={isLoading}
                style={inputStyle(!!error)}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="password" style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500, color: BRAND.text }}>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder={tab === "signin" ? "Your password" : "At least 8 characters"}
                required
                minLength={tab === "signup" ? 8 : undefined}
                disabled={isLoading}
                style={inputStyle(!!error)}
              />
            </div>
            {tab === "signup" && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="passwordConfirm" style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500, color: BRAND.text }}>
                    Confirm password
                  </label>
                  <input
                    id="passwordConfirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => { setPasswordConfirm(e.target.value); setError(""); }}
                    placeholder="Repeat password"
                    required
                    disabled={isLoading}
                    style={inputStyle(!!error)}
                  />
                </div>
                {turnstileSiteKey && (
                  <div style={{ marginBottom: 16 }}>
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={turnstileSiteKey}
                      onSuccess={setTurnstileToken}
                      onExpire={() => setTurnstileToken("")}
                      options={{ theme: "dark", size: "normal" }}
                    />
                  </div>
                )}
              </>
            )}

            {error && (
              <div style={{ padding: 12, marginBottom: 16, background: "rgba(239, 68, 68, 0.1)", border: `1px solid ${BRAND.danger}`, borderRadius: 8, color: BRAND.danger, fontSize: 14, textAlign: "center" }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: "100%",
                padding: 14,
                background: isLoading ? BRAND.border : BRAND.accent,
                color: BRAND.text,
                border: "none",
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? "Please wait..." : tab === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
